import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { ButtonToolbar } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { SubmissionError } from "redux-form";
import classNames from "classnames";

import { CheckboxButton, ArchiveButton, EditButton } from "../../../common";
import { DataTable } from "../../../common/DataTable";
import BasicGroupFields from "./BasicGroupFields";

import { GROUPS } from "../../../../utils/data/models";
import * as dataActions from "../../../../utils/data/dataActions";
import * as types from "../../../../utils/data/dataActionTypes";

class GroupsDataTable extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.archiveGroup = this.archiveGroup.bind(this);
    this.createEmptyGroupModel = this.createEmptyGroupModel.bind(this);
    this.createGroup = this.createGroup.bind(this);
    this.setCellClassNames = this.setCellClassNames.bind(this);
    this.toggleArchivedGroups = this.toggleArchivedGroups.bind(this);
    this.updateGroup = this.updateGroup.bind(this);
    this.getData = this.getData.bind(this);
  }

  componentWillMount() {
    this.props.dataActions.clearDataList(GROUPS);
  }

  archiveGroup(groupId, isArchived) {
    // Toggle archive status and refresh the list
    this.props.dataActions
      .updateDataItem(GROUPS, { groupId, isArchived: !isArchived })
      .then(() => {
        this.getData(
          this.props.offset,
          this.props.limit,
          this.props.q,
          this.props.sortBy,
          this.props.filters
        );
      });
  }

  createEmptyGroupModel() {
    return { name: "" };
  }

  createGroup(values) {
    // Return the promise so redux-form can catch SubmissionError thrown inside getData
    return this.props.dataActions
      .createDataItem(GROUPS, values)
      .then((action) => {
        this.getData(
          this.props.offset,
          this.props.limit,
          this.props.q,
          this.props.sortBy,
          this.props.filters,
          action
        );
      });
  }

  setCellClassNames(group, customClass = false) {
    // Apply 'archived' CSS class to visually grey out archived group rows
    return classNames({
      archived: group.isArchived,
      [customClass]: !!customClass,
    });
  }

  toggleArchivedGroups() {
    // Toggle the archive filter: when unchecked hides archived groups, when checked shows them
    const isArchivedFiltered = this.props.filters.includes(GROUPS.archiveFilter);
    const filters = isArchivedFiltered
      ? this.props.filters.filter((filter) => filter !== GROUPS.archiveFilter)
      : [...this.props.filters, GROUPS.archiveFilter];
    this.getData(
      this.props.offset,
      this.props.limit,
      this.props.q,
      this.props.sortBy,
      filters
    );
  }

  updateGroup(values) {
    // Return the promise so redux-form can catch SubmissionError thrown inside getData
    return this.props.dataActions
      .putDataItem(GROUPS, values)
      .then((action) => {
        this.getData(
          this.props.offset,
          this.props.limit,
          this.props.q,
          this.props.sortBy,
          this.props.filters,
          action
        );
      });
  }

  getCols() {
    return [
      {
        name: <FormattedMessage id="admin.groups.datatable.columns.name" />,
        key: "name",
        display: (group) => (
          <span className={this.setCellClassNames(group, "group-name")}>{group.name}</span>
        ),
        widthPct: 35,
      },
      {
        name: (
          <FormattedMessage id="admin.groups.datatable.columns.createdBy" />
        ),
        key: "createdByUser.username",
        display: (group) => (
          <span className={this.setCellClassNames(group)}>
            {group.createdByUser ? group.createdByUser.username : ""}
          </span>
        ),
        widthPct: 30,
      },
      {
        name: (
          <FormattedMessage id="admin.groups.datatable.columns.createdAt" />
        ),
        key: "createdAt",
        display: (group) => (
          <span className={this.setCellClassNames(group)}>
            {group.createdAt ? group.createdAt.split("T")[0] : ""}
          </span>
        ),
        widthPct: 20,
      },
      {
        name: <FormattedMessage id="datatable.actions" />,
        display: (group) => this.generateActionButtons(group),
        widthPct: 15,
      },
    ];
  }

  renderFormattedMessage(icon) {
    return <span className="icon warning">{icon}</span>;
  }

  generateActionButtons(group) {
    return (
      <ButtonToolbar key={group.groupId}>
        <EditButton
          title={!group.isArchived ? <FormattedMessage id="admin.groups.datatable.actions.edit" /> : <FormattedMessage id="admin.groups.datatable.actions.view" />}
          confirmationTitle={
            <FormattedMessage id="admin.blocs.datatable.actions.showConfirm.title">
              {this.renderFormattedMessage}
            </FormattedMessage>
          }
          confirmationMessage={
            <FormattedMessage id="admin.blocs.datatable.actions.showConfirm.message" />
          }
          closeButtonKey="common.cancel"
          requiresCloseConfirmation={!group.isArchived}
          confirmOnClose={!group.isArchived}
          allowSubmission={!group.isArchived}
          readOnly={group.isArchived}
          form={BasicGroupFields}
          label={<FormattedMessage id={group.isArchived ? "common.view" : "common.edit"} />}
          data={group}
          editFunction={this.updateGroup}
        />
        {!group.isArchived && (
          <ArchiveButton
            isArchived={group.isArchived}
            archivedName={group.name}
            warningMessage={
              <FormattedMessage id="admin.groups.datatable.actions.archiveWarning" />
            }
            archiveFunction={this.archiveGroup.bind(
              null,
              group.groupId,
              group.isArchived
            )}
            label={<FormattedMessage id="common.archive" />}
          />
        )}
      </ButtonToolbar>
    );
  }

  generateAddButton() {
    return (
      <div>
        <CheckboxButton
          className="include-archived"
          id="archived"
          label={
            <FormattedMessage id="admin.groups.datatable.actions.includeArchivedGroups" />
          }
          checked={!this.props.filters.includes(GROUPS.archiveFilter)}
          onClick={this.toggleArchivedGroups}
        />
        <EditButton
          title={
            <FormattedMessage id="admin.groups.datatable.actions.add" />
          }
          confirmationTitle={
            <FormattedMessage id="admin.blocs.datatable.actions.showConfirm.title">
              {this.renderFormattedMessage}
            </FormattedMessage>
          }
          confirmationMessage={
            <FormattedMessage id="admin.blocs.datatable.actions.showConfirm.message" />
          }
          closeButtonKey="common.cancel"
          requiresCloseConfirmation={true}
          confirmOnClose={true}
          label={<FormattedMessage id="common.add" />}
          add={true}
          form={BasicGroupFields}
          data={this.createEmptyGroupModel()}
          editFunction={this.createGroup}
        />
      </div>
    );
  }

  getData(offset, limit, q, sortBy, filters, action = {}) {
    switch (action.type) {
      case types.CREATE_DATA_ITEM_FAILURE:
      case types.UPDATE_DATA_ITEM_FAILURE: {
        // Show an inline field error when the API returns a duplicate name error
        if (
          action.payload.error.data &&
          action.payload.error.data.detail === "duplicateGroupName"
        ) {
          throw new SubmissionError({
            name: {
              messageKey:
                "validation.customMessages.serverErrors.duplicateGroupName",
              values: {},
              state: "error",
            },
          });
        }
        // Fall back to a generic inline field error for other failures
        throw new SubmissionError({
          name: {
            messageKey: "validation.customMessages.serverErrors.genericError",
            values: {},
            state: "error",
          },
        });
      }
      default: {
        return this.props.dataActions.getDataList(
          GROUPS,
          offset,
          limit,
          q,
          sortBy,
          filters
        );
      }
    }
  }

  render() {
    return (
      <DataTable
        ref="dataTable"
        title={<FormattedMessage id="admin.groups.title" />}
        getData={this.getData}
        cols={this.getCols()}
        q={this.props.q}
        filters={this.props.filters}
        offset={this.props.offset}
        data={this.props.items}
        rowsPerPage={this.props.limit}
        totalRows={this.props.total}
        sortBy={this.props.sortBy}
        addButton={this.generateAddButton()}
      />
    );
  }
}

GroupsDataTable.propTypes = {
  active: PropTypes.object,
  dataActions: PropTypes.object,
  filters: PropTypes.array,
  items: PropTypes.array,
  q: PropTypes.string,
  limit: PropTypes.number,
  offset: PropTypes.number,
  sortBy: PropTypes.array,
  total: PropTypes.number,
};

GroupsDataTable.defaultProps = { limit: 10 };

function mapStateToProps(state) {
  const data = state.data.groups || {};
  const pagination = data.pagination || {};
  return {
    active: data.active || {},
    // Default to hiding archived groups; user can toggle via checkbox
    filters: data.filters || [GROUPS.archiveFilter],
    limit: pagination.limit || 100,
    offset: pagination.offset || 0,
    q: data.q,
    sortBy: data.sortBy || ["name:asc"],
    total: pagination.totalCount || 0,
    items: data.items || null,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dataActions: bindActionCreators(dataActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(GroupsDataTable);



// WEBPACK FOOTER //
// ./src/components/dashboard/admin/groups/GroupsDataTable.js